import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import axios from 'axios';
import { Users as UsersIcon, Plus, Trash, Pencil, UserCircle, Shield, Eye, EyeSlash, Globe } from '@phosphor-icons/react';
import { ArrowUp, ArrowDown, ArrowUpDown, Check, Eye as EyeIcon, EyeOff, Activity } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
  ContextMenuSeparator,
} from '@/components/ui/context-menu';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Default column widths for Users table
const DEFAULT_USER_COLUMN_WIDTHS = {
  name: 200,
  email: 250,
  department: 150,
  role: 150,
  projects: 150,
  actions: 100
};

// Default column widths for Portal Users table
const DEFAULT_PORTAL_COLUMN_WIDTHS = {
  username: 150,
  password: 120,
  email: 200,
  company: 150,
  project: 150,
  linked: 180,
  actions: 100
};

// User roles with descriptions
const USER_ROLES = {
  admin: { label: 'Admin', color: 'bg-red-100 text-red-800', desc: 'Full system access' },
  document_controller: { label: 'Document Controller', color: 'bg-purple-100 text-purple-800', desc: 'Native files + DDM access' },
  manager: { label: 'Manager', color: 'bg-blue-100 text-blue-800', desc: 'Review approvals, PDF only' },
  reviewer: { label: 'Reviewer', color: 'bg-green-100 text-green-800', desc: 'Review assigned docs, PDF only' },
  user: { label: 'User', color: 'bg-yellow-100 text-yellow-800', desc: 'View all, PDF only' },
  viewer: { label: 'Viewer', color: 'bg-gray-100 text-gray-800', desc: 'View & download PDF only' }
};

// Permission definitions with descriptions
const PERMISSIONS = {
  can_view_all_documents: { label: 'View Documents', desc: 'Access to view documents section' },
  can_view_transmittals: { label: 'View Transmittals', desc: 'Access to view transmittals section' },
  can_view_correspondence: { label: 'View Correspondence', desc: 'Access to correspondence section' },
  can_view_queries: { label: 'View Queries', desc: 'Access to queries section' },
  can_download_pdf: { label: 'Download PDF', desc: 'Download PDF versions of documents' },
  can_download_native: { label: 'Download Native Files', desc: 'Download original/native file formats' },
  can_view_comments: { label: 'View Comments', desc: 'View review comments and markups' },
  can_download_comments: { label: 'Download Comments', desc: 'Download comments report as PDF/Excel' },
  can_edit_documents: { label: 'Edit Documents', desc: 'Create, edit, and delete documents' },
  can_upload_revision: { label: 'Upload Revision', desc: 'Upload new revisions to documents' },
  can_delete_revision: { label: 'Delete Revision', desc: 'Delete revisions from documents' },
  can_manage_ddm: { label: 'Manage DDM', desc: 'Access Document Data Matrix settings' },
  can_manage_users: { label: 'Manage Users', desc: 'Create, edit, and delete users' },
  can_approve_reviews: { label: 'Approve Reviews', desc: 'Approve document review cycles' },
  can_view_mdr: { label: 'View MDR', desc: 'View the Master Document Register (read-only)' },
  can_manage_mdr: { label: 'Manage MDR', desc: 'Import, add, edit, delete MDR entries' },
  can_multi_search: { label: 'Multi-Doc Search', desc: 'Bulk search by many document numbers at once' },
  // v26b: can_export_excel
  can_export_excel: { label: 'Export to Excel', desc: 'Export documents list as Excel report' },
  can_create_transmittals: { label: 'Create Transmittals', desc: 'Create incoming and outgoing transmittals' }
};

// Default permissions for each role
const ROLE_PERMISSIONS = {
  admin: {
    can_view_all_documents: true,
    can_view_transmittals: true,
    can_view_correspondence: true,
    can_view_queries: true,
    can_download_pdf: true,
    can_download_native: true,
    can_view_comments: true,
    can_download_comments: true,
    can_edit_documents: true,
    can_upload_revision: true,
    can_delete_revision: true,
    can_manage_ddm: true,
    can_manage_users: true,
    can_approve_reviews: true,
    can_export_excel: true,
    can_create_transmittals: true,
    can_view_mdr: true,
    can_manage_mdr: true,
    can_multi_search: true
  },
  document_controller: {
    can_view_all_documents: true,
    can_view_transmittals: true,
    can_view_correspondence: true,
    can_view_queries: true,
    can_download_pdf: true,
    can_download_native: true,
    can_view_comments: true,
    can_download_comments: true,
    can_edit_documents: true,
    can_upload_revision: true,
    can_delete_revision: true,
    can_manage_ddm: true,
    can_manage_users: false,
    can_approve_reviews: true,
    can_export_excel: true,
    can_create_transmittals: true,
    can_view_mdr: true,
    can_manage_mdr: true,
    can_multi_search: true
  },
  manager: {
    can_view_all_documents: true,
    can_view_transmittals: true,
    can_view_correspondence: true,
    can_view_queries: true,
    can_download_pdf: true,
    can_download_native: false,
    can_view_comments: true,
    can_download_comments: true,
    can_edit_documents: true,
    can_upload_revision: true,
    can_delete_revision: false,
    can_manage_ddm: false,
    can_manage_users: false,
    can_approve_reviews: true,
    can_export_excel: false,
    can_create_transmittals: false,
    can_view_mdr: true,
    can_manage_mdr: true,
    can_multi_search: false
  },
  reviewer: {
    can_view_all_documents: true,
    can_view_transmittals: true,
    can_view_correspondence: false,
    can_view_queries: false,
    can_download_pdf: true,
    can_download_native: false,
    can_view_comments: true,
    can_download_comments: false,
    can_edit_documents: false,
    can_upload_revision: false,
    can_delete_revision: false,
    can_manage_ddm: false,
    can_manage_users: false,
    can_approve_reviews: false,
    can_export_excel: false,
    can_create_transmittals: false,
    can_view_mdr: true,
    can_manage_mdr: false,
    can_multi_search: false
  },
  user: {
    can_view_all_documents: true,
    can_view_transmittals: false,
    can_view_correspondence: false,
    can_view_queries: false,
    can_download_pdf: true,
    can_download_native: false,
    can_view_comments: false,
    can_download_comments: false,
    can_edit_documents: false,
    can_upload_revision: false,
    can_delete_revision: false,
    can_manage_ddm: false,
    can_manage_users: false,
    can_approve_reviews: false,
    can_export_excel: false,
    can_create_transmittals: false,
    can_view_mdr: false,
    can_manage_mdr: false,
    can_multi_search: false
  },
  viewer: {
    can_view_all_documents: true,
    can_view_transmittals: false,
    can_view_correspondence: false,
    can_view_queries: false,
    can_download_pdf: true,
    can_download_native: false,
    can_view_comments: false,
    can_download_comments: false,
    can_edit_documents: false,
    can_upload_revision: false,
    can_delete_revision: false,
    can_manage_ddm: false,
    can_manage_users: false,
    can_approve_reviews: false,
    can_export_excel: false,
    can_create_transmittals: false,
    can_view_mdr: true,
    can_manage_mdr: false,
    can_multi_search: false
  }
};

// Sort icon component - moved outside main component
const SortIconComponent = ({ column, sortColumn, sortDirection }) => {
  if (sortColumn !== column) {
    return <ArrowUpDown className="h-3 w-3 text-gray-400 ml-1" />;
  }
  return sortDirection === 'asc' 
    ? <ArrowUp className="h-3 w-3 text-primary ml-1" />
    : <ArrowDown className="h-3 w-3 text-primary ml-1" />;
};


// v24: activity log tab component
const ActivityLogTab = ({ API }) => {
  const [summary, setSummary] = React.useState([]);
  const [online, setOnline] = React.useState([]);
  const [recent, setRecent] = React.useState([]);
  const [loadingAct, setLoadingAct] = React.useState(true);
  const [actionFilter, setActionFilter] = React.useState('');
  const [userFilter, setUserFilter] = React.useState('');

  const fetchAll = React.useCallback(async () => {
    try {
      const [s, o, r] = await Promise.all([
        axios.get(`${API}/admin/activity/summary`),
        axios.get(`${API}/admin/activity/online`),
        axios.get(`${API}/admin/activity/recent`, {
          params: { limit: 200, action: actionFilter || undefined, user_id: userFilter || undefined },
        }),
      ]);
      setSummary(s.data?.users || []);
      setOnline(o.data?.online || []);
      setRecent(r.data?.items || []);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 403) toast.error('Admin access required to view activity log');
    } finally {
      setLoadingAct(false);
    }
  }, [API, actionFilter, userFilter]);

  React.useEffect(() => {
    fetchAll();
    const t = setInterval(fetchAll, 30000); // auto-refresh every 30s
    return () => clearInterval(t);
  }, [fetchAll]);

  const fmtTs = (iso) => {
    if (!iso) return '-';
    try { return new Date(iso).toLocaleString(); } catch (e) { return iso; }
  };
  const fmtRelative = (iso) => {
    if (!iso) return '-';
    const ms = Date.now() - new Date(iso).getTime();
    if (ms < 60000) return `${Math.floor(ms/1000)}s ago`;
    if (ms < 3600000) return `${Math.floor(ms/60000)}m ago`;
    if (ms < 86400000) return `${Math.floor(ms/3600000)}h ago`;
    return `${Math.floor(ms/86400000)}d ago`;
  };

  return (
    <div className="space-y-6" data-testid="activity-log-content">
      {/* Online users */}
      <div className="bg-white border border-gray-200 rounded-2xl p-5 shadow-sm">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base font-semibold text-gray-900 flex items-center">
            <span className="inline-block w-2 h-2 rounded-full bg-green-500 mr-2 animate-pulse" />
            Online now ({online.length})
          </h3>
          <span className="text-xs text-gray-500">active within last 5 min</span>
        </div>
        {online.length === 0 ? (
          <div className="text-sm text-gray-500 py-3" data-testid="online-empty">No users currently online.</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {online.map((o) => (
              <div key={o.user_id} className="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-xl" data-testid={`online-user-${o.user_id}`}>
                <span className="inline-block w-2 h-2 rounded-full bg-green-500" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-900 truncate">{o.user_name}</div>
                  <div className="text-xs text-gray-500 truncate">{o.last_path}</div>
                </div>
                <div className="text-xs text-gray-500 whitespace-nowrap">{fmtRelative(o.last_seen)}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* User summary */}
      <div className="bg-white border border-gray-200 rounded-2xl p-5 shadow-sm">
        <h3 className="text-base font-semibold text-gray-900 mb-3">User activity summary</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="activity-summary-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-gray-500 border-b border-gray-200">
                <th className="py-2 pr-3">User</th>
                <th className="py-2 pr-3">Role</th>
                <th className="py-2 pr-3">Status</th>
                <th className="py-2 pr-3">Last login</th>
                <th className="py-2 pr-3">Last seen</th>
                <th className="py-2 pr-3 text-right">24h</th>
                <th className="py-2 pr-3 text-right">7d</th>
                <th className="py-2 pr-3 text-right">30d</th>
                <th className="py-2 pr-3 text-right">Logins</th>
              </tr>
            </thead>
            <tbody>
              {summary.length === 0 && (
                <tr><td colSpan={9} className="py-6 text-center text-gray-400">{loadingAct ? 'Loading...' : 'No data'}</td></tr>
              )}
              {summary.map((u) => (
                <tr
                  key={u.user_id}
                  className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer"
                  onClick={() => setUserFilter(u.user_id)}
                  data-testid={`activity-summary-row-${u.user_id}`}
                >
                  <td className="py-2 pr-3">
                    <div className="font-medium text-gray-900">{u.full_name || u.email}</div>
                    <div className="text-xs text-gray-500">{u.email}</div>
                  </td>
                  <td className="py-2 pr-3 text-gray-700">{u.role}</td>
                  <td className="py-2 pr-3">
                    {u.is_online ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-100 text-green-700 text-xs font-medium">
                        <span className="w-1.5 h-1.5 rounded-full bg-green-500" /> Online
                      </span>
                    ) : (
                      <span className="text-xs text-gray-500">Offline</span>
                    )}
                  </td>
                  <td className="py-2 pr-3 text-gray-700">{fmtTs(u.last_login_at)}</td>
                  <td className="py-2 pr-3 text-gray-700">{u.last_seen_at ? fmtRelative(u.last_seen_at) : '-'}</td>
                  <td className="py-2 pr-3 text-right tabular-nums text-gray-700">{u.total_24h}</td>
                  <td className="py-2 pr-3 text-right tabular-nums text-gray-700">{u.total_7d}</td>
                  <td className="py-2 pr-3 text-right tabular-nums text-gray-700">{u.total_30d}</td>
                  <td className="py-2 pr-3 text-right tabular-nums text-gray-700">{u.login_count || 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Recent activity feed */}
      <div className="bg-white border border-gray-200 rounded-2xl p-5 shadow-sm">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
          <h3 className="text-base font-semibold text-gray-900">Recent activity</h3>
          <div className="flex flex-wrap items-center gap-2">
            <select
              className="text-sm border border-gray-200 rounded-lg px-2 py-1.5 bg-white"
              value={actionFilter}
              onChange={(e) => setActionFilter(e.target.value)}
              data-testid="activity-action-filter"
            >
              <option value="">All actions</option>
              <option value="login">Logins only</option>
              <option value="api_call">API calls only</option>
            </select>
            {userFilter && (
              <button
                className="text-xs px-2 py-1 rounded-lg bg-blue-50 text-blue-700 border border-blue-200"
                onClick={() => setUserFilter('')}
                data-testid="activity-clear-user-filter"
              >
                Clear user filter
              </button>
            )}
            <button
              className="text-xs px-2 py-1 rounded-lg bg-gray-100 text-gray-700 border border-gray-200"
              onClick={fetchAll}
              data-testid="activity-refresh"
            >
              Refresh
            </button>
          </div>
        </div>
        <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
          <table className="w-full text-sm" data-testid="activity-feed-table">
            <thead className="sticky top-0 bg-white z-10">
              <tr className="text-left text-xs uppercase tracking-wide text-gray-500 border-b border-gray-200">
                <th className="py-2 pr-3">When</th>
                <th className="py-2 pr-3">User</th>
                <th className="py-2 pr-3">Action</th>
                <th className="py-2 pr-3">Method</th>
                <th className="py-2 pr-3">Path</th>
                <th className="py-2 pr-3 text-right">Status</th>
                <th className="py-2 pr-3">IP</th>
              </tr>
            </thead>
            <tbody>
              {recent.length === 0 && (
                <tr><td colSpan={7} className="py-6 text-center text-gray-400">{loadingAct ? 'Loading...' : 'No activity yet'}</td></tr>
              )}
              {recent.map((r) => {
                const isLogin = r.action === 'login';
                const statusBadge = r.status_code >= 500 ? 'bg-red-100 text-red-700' : r.status_code >= 400 ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700';
                return (
                  <tr key={r.id} className="border-b border-gray-100" data-testid={`activity-row-${r.id}`}>
                    <td className="py-2 pr-3 text-gray-700 whitespace-nowrap">
                      <div>{fmtRelative(r.timestamp)}</div>
                      <div className="text-xs text-gray-400">{fmtTs(r.timestamp)}</div>
                    </td>
                    <td className="py-2 pr-3 text-gray-900">{r.user_name}</td>
                    <td className="py-2 pr-3">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${isLogin ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'}`}>
                        {r.action || 'api_call'}
                      </span>
                    </td>
                    <td className="py-2 pr-3 font-mono text-xs text-gray-700">{r.method}</td>
                    <td className="py-2 pr-3 font-mono text-xs text-gray-700 truncate max-w-md" title={r.path}>{r.path}</td>
                    <td className="py-2 pr-3 text-right">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium tabular-nums ${statusBadge}`}>{r.status_code}</span>
                    </td>
                    <td className="py-2 pr-3 text-xs text-gray-500 truncate max-w-[140px]" title={r.ip}>{r.ip || '-'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};


const Users = ({ user }) => {
  const [users, setUsers] = useState([]);
  const [portalUsers, setPortalUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [addPortalDialogOpen, setAddPortalDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [resetPassword, setResetPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [forcePasswordChange, setForcePasswordChange] = useState(false);
  const [activeTab, setActiveTab] = useState(() => {
    const tab = new URLSearchParams(window.location.search).get('tab');
    return tab === 'pending' ? 'pending' : 'staff';
  });
  const [pendingUsers, setPendingUsers] = useState([]);
  
  // Sorting state
  const [sortColumn, setSortColumn] = useState('full_name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [portalSortColumn, setPortalSortColumn] = useState('username');
  const [portalSortDirection, setPortalSortDirection] = useState('asc');
  
  // Resizable columns state
  const [userColumnWidths, setUserColumnWidths] = useState(DEFAULT_USER_COLUMN_WIDTHS);
  const [portalColumnWidths, setPortalColumnWidths] = useState(DEFAULT_PORTAL_COLUMN_WIDTHS);
  const resizingRef = useRef(null);
  const startXRef = useRef(0);
  const startWidthRef = useRef(0);
  const currentTableRef = useRef('user'); // Track which table is being resized
  
  // Show/hide password state
  const [showPasswords, setShowPasswords] = useState({});
  
  const [newUser, setNewUser] = useState({
    email: '',
    password: '',
    full_name: '',
    role: 'user',
    department: '',
    permissions: { ...ROLE_PERMISSIONS['user'] },
    project_ids: []
  });

  const [newPortalUser, setNewPortalUser] = useState({
    email: '',
    username: '',
    password: '',
    full_name: '',
    linked_user_id: '',
    role: 'reviewer',
    company: '',
    can_view_native_files: false,
    project_id: ''
  });

  // Link portal user state
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [selectedPortalUser, setSelectedPortalUser] = useState(null);
  const [selectedLinkUserId, setSelectedLinkUserId] = useState('');
  
  // Delete confirmation dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deleteType, setDeleteType] = useState('user'); // 'user' or 'portal'

  useEffect(() => {
    fetchUsers();
    fetchPortalUsers();
    fetchProjects();
    fetchPendingUsers();
  }, []);

  const fetchPendingUsers = async () => {
    try {
      const response = await axios.get(`${API}/users/pending`);
      setPendingUsers(response.data || []);
    } catch (error) {
      console.error('Failed to fetch pending account requests:', error);
    }
  };

  const handleApproveUser = async (pendingUser) => {
    try {
      const res = await axios.post(`${API}/users/${pendingUser.id}/approve`, {});
      toast.success(res.data?.message || 'Account approved');
      fetchPendingUsers();
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve account');
    }
  };

  const handleRejectUser = async (pendingUser) => {
    // v52: rejections are recorded rather than deleted, so capture the reason
    const reason = window.prompt(
      `Reject ${pendingUser.full_name || pendingUser.email}?\n\nOptional reason (kept in the audit log):`,
      ''
    );
    if (reason === null) return;
    try {
      const res = await axios.post(`${API}/users/${pendingUser.id}/reject`, { reason });
      toast.success(res.data?.message || 'Account rejected');
      fetchPendingUsers();
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject account');
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data);
    } catch (error) {
      toast.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const fetchPortalUsers = async () => {
    try {
      const response = await axios.get(`${API}/portal/users`);
      setPortalUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch portal users:', error);
    }
  };
  
  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`);
      setProjects(response.data);
    } catch (error) {
      console.error('Failed to fetch projects:', error);
    }
  };
  
  // Get project name by ID
  const getProjectName = (projectId) => {
    const project = projects.find(p => p.id === projectId);
    return project ? project.name : '-';
  };
  
  // Toggle password visibility
  const togglePasswordVisibility = (userId) => {
    setShowPasswords(prev => ({
      ...prev,
      [userId]: !prev[userId]
    }));
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    if (!newUser.email || !newUser.password || !newUser.full_name) {
      toast.error('Please fill in all required fields');
      return;
    }
    try {
      // Include permissions in the user data
      const userData = {
        ...newUser,
        permissions: newUser.permissions
      };
      await axios.post(`${API}/auth/register`, userData);
      toast.success('User created successfully');
      setAddDialogOpen(false);
      setNewUser({ 
        email: '', 
        password: '', 
        full_name: '', 
        role: 'user', 
        department: '',
        permissions: { ...ROLE_PERMISSIONS['user'] }
      });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    }
  };

  // Handle role change - update permissions to default for that role
  const handleRoleChange = (role, isEdit = false) => {
    const defaultPerms = ROLE_PERMISSIONS[role] || ROLE_PERMISSIONS['viewer'];
    if (isEdit) {
      setSelectedUser({ ...selectedUser, role, permissions: { ...defaultPerms } });
    } else {
      setNewUser({ ...newUser, role, permissions: { ...defaultPerms } });
    }
  };

  // Handle individual permission toggle
  const handlePermissionChange = (permKey, isEdit = false) => {
    if (isEdit) {
      setSelectedUser({
        ...selectedUser,
        permissions: {
          ...selectedUser.permissions,
          [permKey]: !selectedUser.permissions?.[permKey]
        }
      });
    } else {
      setNewUser({
        ...newUser,
        permissions: {
          ...newUser.permissions,
          [permKey]: !newUser.permissions[permKey]
        }
      });
    }
  };

  const handleAddPortalUser = async (e) => {
    e.preventDefault();
    if (!newPortalUser.username || !newPortalUser.password) {
      toast.error('Please fill in all required fields');
      return;
    }
    try {
      await axios.post(`${API}/portal/users`, newPortalUser);
      toast.success('Portal user created successfully');
      setAddPortalDialogOpen(false);
      setNewPortalUser({ 
        email: '', 
        username: '', 
        password: '', 
        full_name: '', 
        linked_user_id: '',
        role: 'reviewer',
        company: '',
        can_view_native_files: false
      });
      fetchPortalUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create portal user');
    }
  };

  const handleLinkPortalUser = async () => {
    if (!selectedPortalUser) {
      toast.error('No portal user selected');
      return;
    }
    try {
      const linkId = selectedLinkUserId === 'none' ? '' : selectedLinkUserId;
      await axios.put(`${API}/portal/users/${selectedPortalUser.id}/link?linked_user_id=${linkId}`);
      toast.success(linkId ? 'Portal user linked successfully' : 'Portal user unlinked');
      setLinkDialogOpen(false);
      setSelectedPortalUser(null);
      setSelectedLinkUserId('');
      fetchPortalUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to link portal user');
    }
  };

  const openLinkDialog = (portalUser) => {
    setSelectedPortalUser(portalUser);
    setSelectedLinkUserId(portalUser.linked_user_id || '');
    setLinkDialogOpen(true);
  };

  const handleUpdateRole = async () => {
    if (!selectedUser) return;
    if (resetPassword && resetPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (resetPassword && resetPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    if (resetPassword) {
      const hasUpper = /[A-Z]/.test(resetPassword);
      const hasLower = /[a-z]/.test(resetPassword);
      const hasNum = /[0-9]/.test(resetPassword);
      const hasSpecial = /[!@#$%^&*()_+\-=\[\]{}|;:',.<>?/~`]/.test(resetPassword);
      if (!hasUpper || !hasLower || !hasNum || !hasSpecial) {
        toast.error('Password must contain uppercase, lowercase, number, and special character');
        return;
      }
    }
    try {
      const payload = {
        role: selectedUser.role,
        department: selectedUser.department,
        permissions: selectedUser.permissions
      };
      if (resetPassword) {
        payload.password = resetPassword;
      }
      if (forcePasswordChange) {
        payload.force_password_change = true;
      }
      await axios.put(`${API}/users/${selectedUser.id}`, payload);
      toast.success('User updated successfully');
      setEditDialogOpen(false);
      setSelectedUser(null);
      setResetPassword('');
      setConfirmPassword('');
      setForcePasswordChange(false);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (userId === user?.id) {
      toast.error('You cannot delete your own account');
      return;
    }
    
    // Use AlertDialog instead of window.confirm
    setUserToDelete({ id: userId, name: userName });
    setDeleteType('user');
    setDeleteDialogOpen(true);
  };
  
  const confirmDeleteUser = async () => {
    if (!userToDelete) return;
    
    try {
      await axios.delete(`${API}/users/${userToDelete.id}`);
      toast.success('User deleted successfully');
      fetchUsers();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    } finally {
      setDeleteDialogOpen(false);
      setUserToDelete(null);
    }
  };

  const handleDeletePortalUser = async (userId, userName) => {
    // Use AlertDialog instead of window.confirm
    setUserToDelete({ id: userId, name: userName });
    setDeleteType('portal');
    setDeleteDialogOpen(true);
  };
  
  const confirmDeletePortalUser = async () => {
    if (!userToDelete) return;
    
    try {
      await axios.delete(`${API}/portal/users/${userToDelete.id}`);
      toast.success('Portal user deleted successfully');
      fetchPortalUsers();
    } catch (error) {
      console.error('Delete portal user error:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete portal user');
    } finally {
      setDeleteDialogOpen(false);
      setUserToDelete(null);
    }
  };

  const openEditDialog = (u) => {
    // Load user with their permissions (use role defaults if not set)
    const userPerms = u.permissions || ROLE_PERMISSIONS[u.role] || ROLE_PERMISSIONS['viewer'];
    setSelectedUser({ ...u, permissions: { ...userPerms } });
    setResetPassword('');
    setConfirmPassword('');
    setForcePasswordChange(false);
    setEditDialogOpen(true);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-'; return new Date(String(dateString).endsWith('Z') || String(dateString).includes('+') ? dateString : dateString + 'Z').toLocaleDateString();
  };

  const getRoleBadge = (role) => {
    const roleInfo = USER_ROLES[role] || USER_ROLES.viewer;
    return (
      <span className={`text-xs px-2 py-1 rounded-md font-medium ${roleInfo.color}`}>
        {roleInfo.label}
      </span>
    );
  };

  // Sort handlers
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Column resize handlers
  const startResize = useCallback((tableType, columnKey, e) => {
    e.preventDefault();
    e.stopPropagation();
    resizingRef.current = columnKey;
    currentTableRef.current = tableType;
    startXRef.current = e.clientX;
    const widths = tableType === 'user' ? userColumnWidths : portalColumnWidths;
    startWidthRef.current = widths[columnKey] || 150;

    const handleMouseMove = (moveEvent) => {
      if (!resizingRef.current) return;
      const diff = moveEvent.clientX - startXRef.current;
      const newWidth = Math.max(50, startWidthRef.current + diff);
      
      if (currentTableRef.current === 'user') {
        setUserColumnWidths(prev => ({ ...prev, [resizingRef.current]: newWidth }));
      } else {
        setPortalColumnWidths(prev => ({ ...prev, [resizingRef.current]: newWidth }));
      }
    };

    const handleMouseUp = () => {
      resizingRef.current = null;
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, [userColumnWidths, portalColumnWidths]);

  const handlePortalSort = (column) => {
    if (portalSortColumn === column) {
      setPortalSortDirection(portalSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setPortalSortColumn(column);
      setPortalSortDirection('asc');
    }
  };

  // Sorted users
  const sortedUsers = useMemo(() => {
    return [...users].sort((a, b) => {
      let aVal = a[sortColumn];
      let bVal = b[sortColumn];
      
      if (aVal == null) aVal = '';
      if (bVal == null) bVal = '';
      
      if (sortColumn === 'created_at') {
        aVal = new Date(aVal || 0).getTime();
        bVal = new Date(bVal || 0).getTime();
      } else if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = (bVal || '').toLowerCase();
      }
      
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [users, sortColumn, sortDirection]);

  const sortedPortalUsers = useMemo(() => {
    return [...portalUsers].sort((a, b) => {
      let aVal = a[portalSortColumn];
      let bVal = b[portalSortColumn];
      
      if (aVal == null) aVal = '';
      if (bVal == null) bVal = '';
      
      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = (bVal || '').toLowerCase();
      }
      
      if (aVal < bVal) return portalSortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return portalSortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [portalUsers, portalSortColumn, portalSortDirection]);

  // Check if user can manage users
  // Multiple checks for backward compatibility:
  // 1. Super admin flag
  // 2. Role is 'admin' (case-insensitive)
  // 3. Has can_manage_users permission
  const canManageUsers = 
    user?.is_super_admin === true || 
    user?.role?.toLowerCase() === 'admin' ||
    user?.permissions?.can_manage_users === true;

  if (loading) {
    return <div className="p-6">Loading...</div>;
  }

  return (
    <div className="max-w-6xl mx-auto px-6 py-8" data-testid="users-page">
      <div className="mb-6">
        <h1 className="text-3xl font-semibold text-gray-900 mb-1">Users</h1>
        <p className="text-gray-500">Manage staff and portal users</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="mb-6 bg-gray-100 p-1 rounded-xl">
          <TabsTrigger value="staff" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm">
            <UsersIcon size={16} className="mr-2" />
            Staff Users ({users.length})
          </TabsTrigger>
          <TabsTrigger value="portal" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm">
            <Globe size={16} className="mr-2" />
            Portal Users ({portalUsers.length})
          </TabsTrigger>
                  <TabsTrigger value="activity" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="activity-log-tab">
            <Activity size={16} className="mr-2" />
            Activity Log
          </TabsTrigger>
          {canManageUsers && (
            <TabsTrigger value="pending" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="pending-approvals-tab">
              <UsersIcon size={16} className="mr-2" />
              Pending Approvals{pendingUsers.length > 0 ? ` (${pendingUsers.length})` : ''}
            </TabsTrigger>
          )}
        </TabsList>

        {/* Pending Account Requests Tab */}
        {canManageUsers && (
        <TabsContent value="pending">
          <div className="bg-white rounded-2xl border border-gray-100 shadow-apple-sm p-6" data-testid="pending-approvals-panel">
            <h3 className="text-base font-semibold text-gray-900 mb-1">Account requests</h3>
            <p className="text-sm text-gray-500 mb-5">
              These accounts cannot log in until a system administrator approves them.
            </p>
            {pendingUsers.length === 0 ? (
              <p className="text-sm text-gray-500 py-6 text-center" data-testid="no-pending-requests">
                No pending account requests.
              </p>
            ) : (
              <div className="space-y-3">
                {pendingUsers.map((pu) => (
                  <div
                    key={pu.id}
                    className="flex items-center justify-between gap-4 p-4 border border-amber-200 bg-amber-50 rounded-xl"
                    data-testid={`pending-user-${pu.id}`}
                  >
                    <div className="min-w-0">
                      <p className="font-medium text-gray-900 truncate">{pu.full_name || pu.email}</p>
                      <p className="text-sm text-gray-600 truncate">{pu.email}</p>
                      {pu.department && <p className="text-xs text-gray-500">Department: {pu.department}</p>}
                      <span
                        className="inline-block mt-1 text-xs px-2 py-0.5 rounded-full bg-white border border-amber-300 text-amber-800"
                        data-testid={`pending-reason-${pu.id}`}
                      >
                        {pu.pending_reason === 'admin_created'
                          ? `Created by ${pu.created_by_email || 'an admin'}`
                          : 'Sign-up request'}
                      </span>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <Button
                        size="sm"
                        onClick={() => handleApproveUser(pu)}
                        className="bg-green-600 hover:bg-green-700 text-white rounded-lg"
                        data-testid={`approve-user-${pu.id}`}
                      >
                        Approve
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleRejectUser(pu)}
                        className="rounded-lg border-red-200 text-red-600 hover:bg-red-50"
                        data-testid={`reject-user-${pu.id}`}
                      >
                        Reject
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>
        )}


        {/* Staff Users Tab */}
        <TabsContent value="staff">
          <div className="flex items-center justify-between mb-4">
            <div className="flex flex-wrap gap-2 text-xs">
              {Object.entries(USER_ROLES).map(([key, value]) => (
                <span key={key} className={`px-2.5 py-1 rounded-lg ${value.color}`}>
                  {value.label}
                </span>
              ))}
            </div>
            {canManageUsers && (
              <Button size="sm" onClick={() => setAddDialogOpen(true)} className="bg-blue-900 hover:bg-blue-800 rounded-xl h-9" data-testid="add-user-btn">
                <Plus size={14} className="mr-1" />
                Add Staff User
              </Button>
            )}
          </div>

          {users.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-2xl border border-gray-100 shadow-apple-sm">
              <UsersIcon size={40} className="mx-auto mb-3 text-gray-300" />
              <p className="text-gray-500">No staff users found</p>
            </div>
          ) : (
            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-apple-sm">
              <div className="grid grid-cols-12 gap-4 px-4 py-3 bg-gray-50 border-b border-gray-100">
                <button onClick={() => handleSort('full_name')} className="col-span-4 flex items-center text-sm font-semibold text-gray-700 hover:text-gray-900">
                  User <SortIconComponent column="full_name" sortColumn={sortColumn} sortDirection={sortDirection} />
                </button>
                <button onClick={() => handleSort('role')} className="col-span-3 flex items-center text-sm font-semibold text-gray-700 hover:text-gray-900">
                  Role <SortIconComponent column="role" sortColumn={sortColumn} sortDirection={sortDirection} />
                </button>
                <button onClick={() => handleSort('department')} className="col-span-2 flex items-center text-sm font-semibold text-gray-700 hover:text-gray-900">
                  Department <SortIconComponent column="department" sortColumn={sortColumn} sortDirection={sortDirection} />
                </button>
                <button onClick={() => handleSort('created_at')} className="col-span-2 flex items-center text-sm font-semibold text-gray-700 hover:text-gray-900">
                  Joined <SortIconComponent column="created_at" sortColumn={sortColumn} sortDirection={sortDirection} />
                </button>
                <div className="col-span-1 text-sm font-semibold text-gray-700">Actions</div>
              </div>
              <div className="divide-y divide-gray-50">
                {sortedUsers.map((u) => (
                  <ContextMenu key={u.id}>
                    <ContextMenuTrigger asChild>
                      <div className="grid grid-cols-12 gap-4 px-4 py-3 items-center hover:bg-gray-50 transition-colors cursor-context-menu" data-testid={`user-row-${u.id}`}>
                    <div className="col-span-4 flex items-center gap-3">
                      <div className="w-9 h-9 bg-gradient-to-br from-blue-800 to-blue-900 rounded-full flex items-center justify-center text-white text-sm font-medium">
                        {u.full_name.charAt(0).toUpperCase()}
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium text-sm text-gray-900 truncate">{u.full_name}</p>
                        <p className="text-xs text-gray-500 truncate">{u.email}</p>
                      </div>
                    </div>
                    <div className="col-span-3">
                      {getRoleBadge(u.role)}
                    </div>
                    <div className="col-span-2 text-sm text-gray-600">
                      {u.department || '-'}
                    </div>
                    <div className="col-span-2 text-sm text-gray-500">
                      {formatDate(u.created_at)}
                    </div>
                    <div className="col-span-1 flex gap-1">
                      {canManageUsers && u.id !== user?.id && (
                        <>
                          <button
                            onClick={() => openEditDialog(u)}
                            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                            title="Edit"
                          >
                            <Pencil size={14} />
                          </button>
                          <button
                            onClick={() => handleDeleteUser(u.id, u.full_name)}
                            className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                            title="Delete"
                          >
                            <Trash size={14} />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                    </ContextMenuTrigger>
                    <ContextMenuContent className="w-48">
                      <ContextMenuItem onClick={() => openEditDialog(u)} className="cursor-pointer">
                        <Pencil size={14} className="mr-2" />
                        Edit User
                      </ContextMenuItem>
                      <ContextMenuSeparator />
                      {u.id !== user?.id && (
                        <ContextMenuItem onClick={() => handleDeleteUser(u.id, u.full_name)} className="cursor-pointer text-red-600">
                          <Trash size={14} className="mr-2" />
                          Delete User
                        </ContextMenuItem>
                      )}
                    </ContextMenuContent>
                  </ContextMenu>
                ))}
              </div>
            </div>
          )}
        </TabsContent>

        {/* Portal Users Tab */}
        <TabsContent value="portal">
          <div className="flex items-center justify-between mb-4">
            <div className="flex flex-wrap gap-2 text-xs">
              <span className="px-2.5 py-1 rounded-lg bg-green-100 text-green-800">Reviewer</span>
              <span className="px-2.5 py-1 rounded-lg bg-blue-100 text-blue-800">Project Manager</span>
              <span className="px-2.5 py-1 rounded-lg bg-purple-100 text-purple-800">Document Controller</span>
            </div>
            {canManageUsers && (
              <Button size="sm" onClick={() => setAddPortalDialogOpen(true)} className="bg-blue-900 hover:bg-blue-800 rounded-xl h-9" data-testid="add-portal-user-btn">
                <Plus size={14} className="mr-1" />
                Add Portal User
              </Button>
            )}
          </div>

          {portalUsers.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-2xl border border-gray-100 shadow-apple-sm">
              <Globe size={40} className="mx-auto mb-3 text-gray-300" />
              <p className="text-gray-500 mb-2">No portal users yet</p>
              <p className="text-xs text-gray-400">Create portal users to give external access to documents</p>
            </div>
          ) : (
            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-apple-sm">
              <div className="grid grid-cols-12 gap-3 px-4 py-3 bg-gray-50 border-b border-gray-100">
                <button onClick={() => handlePortalSort('username')} className="col-span-2 flex items-center text-sm font-semibold text-gray-700 hover:text-gray-900">
                  Username <SortIconComponent column="username" sortColumn={portalSortColumn} sortDirection={portalSortDirection} />
                </button>
                <div className="col-span-2 text-sm font-semibold text-gray-700">Password</div>
                <button onClick={() => handlePortalSort('company')} className="col-span-2 flex items-center text-sm font-semibold text-gray-700 hover:text-gray-900">
                  Company <SortIconComponent column="company" sortColumn={portalSortColumn} sortDirection={portalSortDirection} />
                </button>
                <button onClick={() => handlePortalSort('role')} className="col-span-1 flex items-center text-sm font-semibold text-gray-700 hover:text-gray-900">
                  Role <SortIconComponent column="role" sortColumn={portalSortColumn} sortDirection={portalSortDirection} />
                </button>
                <div className="col-span-2 text-sm font-semibold text-gray-700">Project</div>
                <button onClick={() => handlePortalSort('is_active')} className="col-span-1 flex items-center text-sm font-semibold text-gray-700 hover:text-gray-900">
                  Status <SortIconComponent column="is_active" sortColumn={portalSortColumn} sortDirection={portalSortDirection} />
                </button>
                <div className="col-span-2 text-sm font-semibold text-gray-700">Actions</div>
              </div>
              <div className="divide-y divide-gray-50">
                {sortedPortalUsers.map((pu) => {
                  const linkedUser = pu.linked_user_id ? users.find(u => u.id === pu.linked_user_id) : null;
                  const roleColors = {
                    'reviewer': 'bg-green-100 text-green-800',
                    'project_manager': 'bg-blue-100 text-blue-800',
                    'document_controller': 'bg-purple-100 text-purple-800'
                  };
                  const roleLabels = {
                    'reviewer': 'Reviewer',
                    'project_manager': 'PM',
                    'document_controller': 'DC'
                  };
                  return (
                  <ContextMenu key={pu.id}>
                    <ContextMenuTrigger asChild>
                      <div className="grid grid-cols-12 gap-3 px-4 py-3 items-center hover:bg-gray-50 transition-colors cursor-context-menu" data-testid={`portal-user-row-${pu.id}`}>
                    <div className="col-span-2 flex items-center gap-2">
                      <div className="w-8 h-8 bg-blue-800 rounded-full flex items-center justify-center text-white text-sm font-medium flex-shrink-0">
                        <Globe size={14} />
                      </div>
                      <span className="font-mono text-sm font-medium text-gray-900 truncate">{pu.username}</span>
                    </div>
                    <div className="col-span-2 flex items-center gap-1">
                      {pu.plain_password ? (
                        <div className="flex items-center gap-1">
                          <span className="font-mono text-sm text-gray-600">
                            {showPasswords[pu.id] ? pu.plain_password : '••••••••'}
                          </span>
                          <button
                            onClick={() => togglePasswordVisibility(pu.id)}
                            className="p-1 text-gray-400 hover:text-gray-600 rounded"
                            title={showPasswords[pu.id] ? "Hide password" : "Show password"}
                          >
                            {showPasswords[pu.id] ? <EyeOff size={14} /> : <Eye size={14} />}
                          </button>
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400 italic">Not visible</span>
                      )}
                    </div>
                    <div className="col-span-2 text-sm text-gray-600 truncate">
                      {pu.company || '-'}
                    </div>
                    <div className="col-span-1">
                      <span className={`text-xs px-2 py-1 rounded-md font-medium ${roleColors[pu.role] || 'bg-gray-100 text-gray-800'}`}>
                        {roleLabels[pu.role] || pu.role || 'Reviewer'}
                      </span>
                    </div>
                    <div className="col-span-2">
                      {pu.project_id ? (
                        <span className="text-xs px-2 py-1 rounded-lg bg-indigo-50 text-indigo-700 font-medium truncate block">
                          {getProjectName(pu.project_id)}
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">All Projects</span>
                      )}
                    </div>
                    <div className="col-span-1">
                      <span className={`text-xs px-2 py-1 rounded-lg font-medium ${pu.is_active !== false ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                        {pu.is_active !== false ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    <div className="col-span-2 flex gap-1">
                      {canManageUsers && (
                        <>
                          <button
                            onClick={() => openLinkDialog(pu)}
                            className="p-2 text-gray-400 hover:text-blue-500 hover:bg-blue-50 rounded-lg transition-colors"
                            title="Link to Internal User"
                          >
                            <UsersIcon size={14} />
                          </button>
                          <button
                            onClick={() => handleDeletePortalUser(pu.id, pu.username)}
                            className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                            title="Delete"
                          >
                            <Trash size={14} />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                    </ContextMenuTrigger>
                    <ContextMenuContent className="w-52">
                      <ContextMenuItem onClick={() => openLinkDialog(pu)} className="cursor-pointer">
                        <UsersIcon size={14} className="mr-2" />
                        Link to Internal User
                      </ContextMenuItem>
                      <ContextMenuSeparator />
                      <ContextMenuItem onClick={() => handleDeletePortalUser(pu.id, pu.username)} className="cursor-pointer text-red-600">
                        <Trash size={14} className="mr-2" />
                        Delete Portal User
                      </ContextMenuItem>
                    </ContextMenuContent>
                  </ContextMenu>
                  );
                })}
              </div>
            </div>
          )}
        </TabsContent>
              {/* v24: activity log tab */}
        <TabsContent value="activity">
          <ActivityLogTab API={API} />
        </TabsContent>
      </Tabs>

      {/* Add Staff User Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent className="max-w-lg rounded-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserCircle size={20} />
              Add Staff User
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddUser} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm">Full Name*</Label>
                <Input
                  value={newUser.full_name}
                  onChange={(e) => setNewUser({ ...newUser, full_name: e.target.value })}
                  placeholder="e.g., John Smith"
                  className="h-10 rounded-lg"
                  required
                  data-testid="new-user-fullname"
                />
              </div>
              <div>
                <Label className="text-sm">Department</Label>
                <Input
                  value={newUser.department}
                  onChange={(e) => setNewUser({ ...newUser, department: e.target.value })}
                  placeholder="e.g., Engineering"
                  className="h-10 rounded-lg"
                  data-testid="new-user-department"
                />
              </div>
            </div>
            <div>
              <Label className="text-sm">Email*</Label>
              <Input
                type="email"
                value={newUser.email}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                placeholder="e.g., john@company.com"
                className="h-10 rounded-lg"
                required
                data-testid="new-user-email"
              />
            </div>
            <div>
              <Label className="text-sm">Password*</Label>
              <Input
                type="password"
                value={newUser.password}
                onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                placeholder="Minimum 6 characters"
                className="h-10 rounded-lg"
                required
                minLength={6}
                data-testid="new-user-password"
              />
            </div>
            <div>
              <Label className="text-sm">Role*</Label>
              <Select value={newUser.role} onValueChange={(val) => handleRoleChange(val, false)}>
                <SelectTrigger className="h-10 rounded-lg" data-testid="new-user-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(USER_ROLES).map(([key, value]) => (
                    <SelectItem key={key} value={key}>
                      <div className="flex items-center gap-2">
                        <span className={`w-2 h-2 rounded-full ${value.color.split(' ')[0]}`}></span>
                        {value.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-1">{USER_ROLES[newUser.role]?.desc}</p>
            </div>
            
            {/* Permissions Section */}
            <div className="border rounded-lg p-4 bg-gray-50">
              <div className="flex items-center gap-2 mb-3">
                <Shield size={16} className="text-blue-600" />
                <Label className="text-sm font-semibold">Permissions</Label>
              </div>
              <div className="space-y-3">
                {Object.entries(PERMISSIONS).map(([key, perm]) => (
                  <div key={key} className="flex items-start gap-3">
                    <Checkbox
                      id={`new-${key}`}
                      checked={newUser.permissions[key] || false}
                      onCheckedChange={() => handlePermissionChange(key, false)}
                      className="mt-0.5"
                      data-testid={`new-perm-${key}`}
                    />
                    <div className="flex-1">
                      <label htmlFor={`new-${key}`} className="text-sm font-medium cursor-pointer">
                        {perm.label}
                      </label>
                      <p className="text-xs text-gray-500">{perm.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <Button type="submit" className="w-full h-10 rounded-lg" data-testid="create-user-btn">
              Create User
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Portal User Dialog */}
      <Dialog open={addPortalDialogOpen} onOpenChange={setAddPortalDialogOpen}>
        <DialogContent className="max-w-md rounded-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Globe size={20} />
              Add Portal User
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddPortalUser} className="space-y-4">
            <div className="p-3 bg-blue-50 rounded-lg text-sm text-blue-700">
              Portal users can access the DocStat Portal to view, download, and review documents.
            </div>
            <div>
              <Label className="text-sm">Username*</Label>
              <Input
                value={newPortalUser.username}
                onChange={(e) => setNewPortalUser({ ...newPortalUser, username: e.target.value })}
                placeholder="e.g., contractor1"
                className="h-10 rounded-lg font-mono"
                required
                data-testid="new-portal-username"
              />
            </div>
            <div>
              <Label className="text-sm">Password*</Label>
              <Input
                type="password"
                value={newPortalUser.password}
                onChange={(e) => setNewPortalUser({ ...newPortalUser, password: e.target.value })}
                placeholder="Minimum 6 characters"
                className="h-10 rounded-lg"
                required
                minLength={6}
                data-testid="new-portal-password"
              />
            </div>
            <div>
              <Label className="text-sm">Role*</Label>
              <Select value={newPortalUser.role} onValueChange={(val) => setNewPortalUser({ ...newPortalUser, role: val })}>
                <SelectTrigger className="h-10 rounded-lg" data-testid="new-portal-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="reviewer">
                    <div className="flex flex-col">
                      <span>Reviewer</span>
                      <span className="text-xs text-gray-500">Can view docs, add markups, finalize reviews</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="project_manager">
                    <div className="flex flex-col">
                      <span>Project Manager</span>
                      <span className="text-xs text-gray-500">Can assign review codes after review</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="document_controller">
                    <div className="flex flex-col">
                      <span>Document Controller</span>
                      <span className="text-xs text-gray-500">Can confirm and close review cycles</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm">Company</Label>
              <Input
                value={newPortalUser.company}
                onChange={(e) => setNewPortalUser({ ...newPortalUser, company: e.target.value })}
                placeholder="e.g., ABC Contractors Ltd"
                className="h-10 rounded-lg"
                data-testid="new-portal-company"
              />
            </div>
            <div>
              <Label className="text-sm">Email (Optional)</Label>
              <Input
                type="email"
                value={newPortalUser.email}
                onChange={(e) => setNewPortalUser({ ...newPortalUser, email: e.target.value })}
                placeholder="e.g., contact@contractor.com"
                className="h-10 rounded-lg"
                data-testid="new-portal-email"
              />
            </div>
            <div>
              <Label className="text-sm">Full Name (Optional)</Label>
              <Input
                value={newPortalUser.full_name}
                onChange={(e) => setNewPortalUser({ ...newPortalUser, full_name: e.target.value })}
                placeholder="e.g., John Smith"
                className="h-10 rounded-lg"
                data-testid="new-portal-fullname"
              />
            </div>
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
              <Checkbox
                id="new-portal-native-files"
                checked={newPortalUser.can_view_native_files || false}
                onCheckedChange={(checked) => setNewPortalUser({ ...newPortalUser, can_view_native_files: checked })}
                data-testid="new-portal-native-files"
              />
              <div>
                <label htmlFor="new-portal-native-files" className="text-sm font-medium cursor-pointer">
                  Can View Native Files
                </label>
                <p className="text-xs text-gray-500">Allow access to download original native file formats (DWG, Excel, etc.)</p>
              </div>
            </div>
            <div>
              <Label className="text-sm">Project</Label>
              <Select 
                value={newPortalUser.project_id || ''} 
                onValueChange={(val) => setNewPortalUser({ ...newPortalUser, project_id: val === 'all' ? '' : val })}
              >
                <SelectTrigger className="h-10 rounded-lg" data-testid="new-portal-project">
                  <SelectValue placeholder="Select project..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Projects</SelectItem>
                  {projects.map(p => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name} ({p.code})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-1">Portal user will only see documents from selected project</p>
            </div>
            <Button type="submit" className="w-full h-10 rounded-lg" data-testid="create-portal-user-btn">
              Create Portal User
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Staff User Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-lg rounded-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil size={18} />
              Edit User
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-500">User</p>
              <p className="font-medium">{selectedUser?.full_name}</p>
              <p className="text-sm text-gray-500">{selectedUser?.email}</p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm">Role</Label>
                <Select 
                  value={selectedUser?.role || 'user'} 
                  onValueChange={(val) => handleRoleChange(val, true)}
                >
                  <SelectTrigger className="h-10 rounded-lg" data-testid="edit-user-role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(USER_ROLES).map(([key, value]) => (
                      <SelectItem key={key} value={key}>
                        <div className="flex items-center gap-2">
                          <span className={`w-2 h-2 rounded-full ${value.color.split(' ')[0]}`}></span>
                          {value.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm">Department</Label>
                <Input
                  value={selectedUser?.department || ''}
                  onChange={(e) => setSelectedUser({ ...selectedUser, department: e.target.value })}
                  placeholder="e.g., Engineering"
                  className="h-10 rounded-lg"
                  data-testid="edit-user-department"
                />
              </div>
            </div>
            
            {/* Permissions Section */}
            <div className="border rounded-lg p-4 bg-gray-50">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Shield size={16} className="text-blue-600" />
                  <Label className="text-sm font-semibold">Permissions</Label>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    const defaultPerms = ROLE_PERMISSIONS[selectedUser?.role] || ROLE_PERMISSIONS['viewer'];
                    setSelectedUser({ ...selectedUser, permissions: { ...defaultPerms } });
                  }}
                  className="text-xs text-blue-600 hover:text-blue-800"
                >
                  Reset to Role Defaults
                </button>
              </div>
              <div className="space-y-3">
                {Object.entries(PERMISSIONS).map(([key, perm]) => (
                  <div key={key} className="flex items-start gap-3">
                    <Checkbox
                      id={`edit-${key}`}
                      checked={selectedUser?.permissions?.[key] || false}
                      onCheckedChange={() => handlePermissionChange(key, true)}
                      className="mt-0.5"
                      data-testid={`edit-perm-${key}`}
                    />
                    <div className="flex-1">
                      <label htmlFor={`edit-${key}`} className="text-sm font-medium cursor-pointer">
                        {perm.label}
                      </label>
                      <p className="text-xs text-gray-500">{perm.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Password Reset Section */}
            <div className="border rounded-lg p-4 bg-amber-50/50">
              <div className="flex items-center gap-2 mb-3">
                <Shield size={16} className="text-amber-600" />
                <Label className="text-sm font-semibold">Password Reset</Label>
              </div>
              <div className="space-y-3">
                <div>
                  <Label className="text-xs text-gray-600">New Password</Label>
                  <div className="relative">
                    <Input
                      type="password"
                      value={resetPassword}
                      onChange={(e) => setResetPassword(e.target.value)}
                      placeholder="Enter new password"
                      className="h-9 rounded-lg pr-10"
                      data-testid="reset-password-input"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-gray-600">Confirm Password</Label>
                  <Input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm new password"
                    className="h-9 rounded-lg"
                    data-testid="confirm-password-input"
                  />
                </div>
                {resetPassword && (
                  <div className="space-y-1">
                    <p className="text-xs font-medium text-gray-600">Password Strength:</p>
                    <div className="grid grid-cols-2 gap-1 text-xs">
                      <span className={resetPassword.length >= 8 ? 'text-green-600' : 'text-red-500'}>
                        {resetPassword.length >= 8 ? '\u2713' : '\u2717'} Min 8 characters
                      </span>
                      <span className={/[A-Z]/.test(resetPassword) ? 'text-green-600' : 'text-red-500'}>
                        {/[A-Z]/.test(resetPassword) ? '\u2713' : '\u2717'} Uppercase letter
                      </span>
                      <span className={/[a-z]/.test(resetPassword) ? 'text-green-600' : 'text-red-500'}>
                        {/[a-z]/.test(resetPassword) ? '\u2713' : '\u2717'} Lowercase letter
                      </span>
                      <span className={/[0-9]/.test(resetPassword) ? 'text-green-600' : 'text-red-500'}>
                        {/[0-9]/.test(resetPassword) ? '\u2713' : '\u2717'} Number
                      </span>
                      <span className={/[!@#$%^&*()_+\-=\[\]{}|;:',.<>?\/~`]/.test(resetPassword) ? 'text-green-600' : 'text-red-500'}>
                        {/[!@#$%^&*()_+\-=\[\]{}|;:',.<>?\/~`]/.test(resetPassword) ? '\u2713' : '\u2717'} Special character
                      </span>
                      <span className={resetPassword && confirmPassword && resetPassword === confirmPassword ? 'text-green-600' : 'text-red-500'}>
                        {resetPassword && confirmPassword && resetPassword === confirmPassword ? '\u2713' : '\u2717'} Passwords match
                      </span>
                    </div>
                  </div>
                )}
                <div className="flex items-center gap-2 pt-1">
                  <Checkbox
                    id="force-password-change"
                    checked={forcePasswordChange}
                    onCheckedChange={(val) => setForcePasswordChange(val)}
                    data-testid="force-password-change"
                  />
                  <label htmlFor="force-password-change" className="text-sm cursor-pointer">
                    Force user to change password on next login
                  </label>
                </div>
              </div>
            </div>
            
            <Button onClick={handleUpdateRole} className="w-full h-10 rounded-lg" data-testid="save-user-btn">
              Save Changes
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Link Portal User to Internal User Dialog */}
      <Dialog open={linkDialogOpen} onOpenChange={setLinkDialogOpen}>
        <DialogContent className="max-w-md rounded-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UsersIcon size={20} />
              Link Portal User to Internal User
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-3 bg-blue-50 rounded-lg text-sm text-blue-700">
              Linking allows portal users to access document reviews assigned to the internal user.
            </div>
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-500">Portal User</p>
              <p className="font-medium">{selectedPortalUser?.username}</p>
              <p className="text-sm text-gray-500">{selectedPortalUser?.email}</p>
            </div>
            <div>
              <Label className="text-sm">Link to Internal User</Label>
              <Select 
                value={selectedLinkUserId} 
                onValueChange={setSelectedLinkUserId}
              >
                <SelectTrigger className="h-10 rounded-lg">
                  <SelectValue placeholder="Select an internal user..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">-- None (Unlink) --</SelectItem>
                  {users.map((u) => (
                    <SelectItem key={u.id} value={u.id}>
                      {u.full_name} ({u.email})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-1">
                The portal user will see reviews assigned to this internal user
              </p>
            </div>
            <Button onClick={handleLinkPortalUser} className="w-full h-10 rounded-lg">
              {selectedLinkUserId ? 'Link User' : 'Unlink User'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation AlertDialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="rounded-xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Trash size={20} className="text-red-500" />
              Confirm Deletion
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete {deleteType === 'portal' ? 'portal user' : 'user'} <strong>&ldquo;{userToDelete?.name}&rdquo;</strong>?
              <br /><br />
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-lg">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={deleteType === 'portal' ? confirmDeletePortalUser : confirmDeleteUser}
              className="bg-red-600 hover:bg-red-700 rounded-lg"
              data-testid="confirm-delete-btn"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Users;
// Deployment marker: 20260126_094916
